import * as Sentry from "@sentry/react";
import { jwtDecode } from "jwt-decode";

export const STARTUP_STALL_TIMEOUT_MS = 15_000;

export type StartupStep =
  | "start"
  | "verify-token"
  | "load-permissions"
  | "load-preferred-language"
  | "verify-temp-token"
  | "set-preferred-language"
  | "client-features";

export type StartupDiagnostics = {
  pendingStep: StartupStep;
  isStandalone: boolean | null;
  hasServiceWorkerController: boolean | null;
  hasAccessToken: boolean;
  hasRefreshToken: boolean;
  accessTokenExpired: boolean | null;
  isOnline: boolean | null;
};

export type StartupEnvironment = {
  matchMedia?: (query: string) => { matches: boolean };
  navigator?: {
    standalone?: boolean;
    onLine?: boolean;
    serviceWorker?: { controller: unknown };
  };
  readStorage: (key: string) => string | null;
  now: () => number;
};

const ACCESS_TOKEN_KEY = "usr_token";
const REFRESH_TOKEN_KEY = "usr_refresh_token";
const SIGN_OUT_KEYS = [
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  "mfa_preauth_token",
];

function isAccessTokenExpired(token: string, now: number): boolean | null {
  try {
    const { exp } = jwtDecode(token);
    return typeof exp === "number" ? exp * 1000 < now : null;
  } catch {
    return null;
  }
}

function readStandalone(env: StartupEnvironment): boolean | null {
  const fromMedia = (() => {
    try {
      return env.matchMedia?.("(display-mode: standalone)").matches;
    } catch {
      return undefined;
    }
  })();
  const fromNavigator = env.navigator?.standalone;

  if (fromMedia === undefined && fromNavigator === undefined) return null;
  return fromMedia === true || fromNavigator === true;
}

/** Never includes token values — only whether they exist and have expired. */
export function collectStartupDiagnostics(
  pendingStep: StartupStep,
  env: StartupEnvironment,
): StartupDiagnostics {
  const accessToken = (env.readStorage(ACCESS_TOKEN_KEY) ?? "").replaceAll(
    '"',
    "",
  );
  const refreshToken = env.readStorage(REFRESH_TOKEN_KEY);
  // The getter throws a SecurityError in sandboxed iframes without allow-same-origin.
  const serviceWorker = (() => {
    try {
      return env.navigator?.serviceWorker;
    } catch {
      return undefined;
    }
  })();

  return {
    pendingStep,
    isStandalone: readStandalone(env),
    hasServiceWorkerController: serviceWorker
      ? Boolean(serviceWorker.controller)
      : null,
    hasAccessToken: accessToken !== "",
    hasRefreshToken: Boolean(refreshToken),
    accessTokenExpired:
      accessToken === "" ? null : isAccessTokenExpired(accessToken, env.now()),
    isOnline:
      typeof env.navigator?.onLine === "boolean" ? env.navigator.onLine : null,
  };
}

export function reportStartupStall(diagnostics: StartupDiagnostics): void {
  Sentry.captureMessage("Startup stalled on the loading spinner", {
    level: "warning",
    tags: {
      startupStep: diagnostics.pendingStep,
      standalone: String(diagnostics.isStandalone),
    },
    extra: diagnostics,
  });
}

export const STARTUP_PROBE_TIMEOUT_MS = 10_000;

export type ProbeOutcome = "ok" | "timeout" | "network-error";

export type ProbeResult = {
  outcome: ProbeOutcome;
  durationMs: number;
};

export type ProbeVerdict = "api-reachable" | "api-unreachable" | "offline";

export type StartupProbeResults = {
  api: ProbeResult;
  app: ProbeResult;
  verdict: ProbeVerdict;
};

export type ProbeEnvironment = {
  fetch: (url: string, init: RequestInit) => Promise<unknown>;
  now: () => number;
  apiOrigin: string;
  appOrigin: string;
};

async function probe(
  url: string,
  env: ProbeEnvironment,
  timeoutMs: number,
): Promise<ProbeResult> {
  const startedAt = env.now();
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  try {
    // no-cors resolves on any HTTP response; Chromium pools uncredentialed requests apart from the stuck one.
    await env.fetch(url, {
      mode: "no-cors",
      credentials: "omit",
      cache: "no-store",
      signal: controller.signal,
    });
    return { outcome: "ok", durationMs: env.now() - startedAt };
  } catch {
    return {
      outcome: timedOut ? "timeout" : "network-error",
      durationMs: env.now() - startedAt,
    };
  } finally {
    clearTimeout(timer);
  }
}

/** Tells a dropped connection from an unreachable API by asking both hosts afresh. */
export async function probeStartupReachability(
  env: ProbeEnvironment,
  timeoutMs = STARTUP_PROBE_TIMEOUT_MS,
): Promise<StartupProbeResults> {
  const cacheBust = `stallProbe=${env.now()}`;
  const [api, app] = await Promise.all([
    probe(`${env.apiOrigin}/app/version?${cacheBust}`, env, timeoutMs),
    probe(`${env.appOrigin}/manifest.json?${cacheBust}`, env, timeoutMs),
  ]);

  const verdict: ProbeVerdict =
    api.outcome === "ok"
      ? "api-reachable"
      : app.outcome === "ok"
        ? "api-unreachable"
        : "offline";

  return { api, app, verdict };
}

export function reportStartupProbe(
  pendingStep: StartupStep,
  results: StartupProbeResults,
): void {
  Sentry.captureMessage("Startup stall probe", {
    level: "warning",
    tags: {
      startupStep: pendingStep,
      probeVerdict: results.verdict,
      probeApi: results.api.outcome,
      probeApp: results.app.outcome,
    },
    extra: results,
  });
}

/** Reports only the first stall of a page load; later ones repeat the same story. */
export function createStallReporter(
  report: (pendingStep: StartupStep) => void,
): (pendingStep: StartupStep) => void {
  let reported = false;
  return (pendingStep) => {
    if (reported) return;
    reported = true;
    report(pendingStep);
  };
}

export type StartupWatchdog = {
  step: (step: StartupStep) => void;
  arm: () => void;
  disarm: () => void;
  complete: () => void;
};

type Timers = {
  setTimeout: (callback: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
};

/**
 * Calls `onStall` with the step still pending when an armed timer runs out.
 * After `complete`, arming is a no-op: later spinners are background refetches.
 */
export function createStartupWatchdog({
  timeoutMs = STARTUP_STALL_TIMEOUT_MS,
  onStall,
  timers = {
    setTimeout: (callback, ms) => globalThis.setTimeout(callback, ms),
    clearTimeout: (handle) =>
      globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>),
  },
}: {
  timeoutMs?: number;
  onStall: (pendingStep: StartupStep) => void;
  timers?: Timers;
}): StartupWatchdog {
  let currentStep: StartupStep = "start";
  let handle: unknown = null;
  let completed = false;

  function disarm() {
    if (handle === null) return;
    timers.clearTimeout(handle);
    handle = null;
  }

  return {
    step(step) {
      currentStep = step;
    },
    arm() {
      if (completed || handle !== null) return;
      handle = timers.setTimeout(() => {
        handle = null;
        onStall(currentStep);
      }, timeoutMs);
    },
    disarm,
    complete() {
      completed = true;
      disarm();
    },
  };
}

export type ResetEnvironment = {
  removeStorage: (key: string) => void;
  serviceWorker?: {
    getRegistrations: () => Promise<readonly { unregister: () => unknown }[]>;
  };
  caches?: {
    keys: () => Promise<string[]>;
    delete: (key: string) => Promise<boolean>;
  };
  reportError: (error: unknown, stage: string) => void;
};

/**
 * Clears what re-adding the home-screen app would clear on our side: the
 * session tokens, any installed service worker and Cache Storage. The stages
 * run independently, so one failing or hanging API cannot block the others.
 */
export async function resetInstalledState(
  env: ResetEnvironment,
): Promise<void> {
  for (const key of SIGN_OUT_KEYS) {
    try {
      env.removeStorage(key);
    } catch (error) {
      env.reportError(error, "storage");
    }
  }

  async function unregisterServiceWorkers() {
    try {
      const registrations = (await env.serviceWorker?.getRegistrations()) ?? [];
      await Promise.all(registrations.map((r) => r.unregister()));
    } catch (error) {
      env.reportError(error, "service-worker");
    }
  }

  async function clearCacheStorage() {
    try {
      const keys = (await env.caches?.keys()) ?? [];
      await Promise.all(keys.map((key) => env.caches?.delete(key)));
    } catch (error) {
      env.reportError(error, "cache-storage");
    }
  }

  await Promise.all([unregisterServiceWorkers(), clearCacheStorage()]);
}

export const RESET_TIMEOUT_MS = 5000;

/**
 * Runs the reset but gives up after `timeoutMs`, so a hung browser API or an
 * environment that throws while being built never keeps the user from `/login`.
 */
export async function resetInstalledStateWithin({
  createEnvironment,
  reportError,
  timeoutMs = RESET_TIMEOUT_MS,
}: {
  createEnvironment: () => ResetEnvironment;
  reportError: (error: unknown, stage: string) => void;
  timeoutMs?: number;
}): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(() => {
      reportError(new Error("Installed-state reset timed out"), "timeout");
      resolve();
    }, timeoutMs);
  });

  try {
    await Promise.race([resetInstalledState(createEnvironment()), timeout]);
  } catch (error) {
    reportError(error, "environment");
  } finally {
    clearTimeout(timer);
  }
}

function readServiceWorkerContainer(): ResetEnvironment["serviceWorker"] {
  try {
    return "serviceWorker" in navigator ? navigator.serviceWorker : undefined;
  } catch {
    return undefined;
  }
}

function reportResetError(error: unknown, stage: string): void {
  Sentry.captureException(error, { tags: { startupResetStage: stage } });
}

export function browserStartupEnvironment(): StartupEnvironment {
  return {
    matchMedia:
      typeof window.matchMedia === "function"
        ? (query) => window.matchMedia(query)
        : undefined,
    navigator: navigator as StartupEnvironment["navigator"],
    readStorage: (key) => {
      try {
        return localStorage.getItem(key);
      } catch {
        return null;
      }
    },
    now: () => Date.now(),
  };
}

export function browserProbeEnvironment(): ProbeEnvironment {
  return {
    fetch: (url, init) => fetch(url, init),
    now: () => Date.now(),
    apiOrigin: import.meta.env.VITE_BE_HOST,
    appOrigin: window.location.origin,
  };
}

export function browserResetEnvironment(): ResetEnvironment {
  return {
    removeStorage: (key) => localStorage.removeItem(key),
    serviceWorker: readServiceWorkerContainer(),
    caches: typeof caches === "undefined" ? undefined : caches,
    reportError: reportResetError,
  };
}

export function resetBrowserInstalledState(): Promise<void> {
  return resetInstalledStateWithin({
    createEnvironment: browserResetEnvironment,
    reportError: reportResetError,
  });
}
